import * as React from "react";

import { cn } from "@/lib/utils";

// Small SVG charts for the Evaluations page. Rates are 0–1; a null is a day
// with no ratings and draws as a gap, never as a zero.

export type Series = { name: string; color: string; data: (number | null)[] };

const pct = (v: number) => `${Math.round(v * 100)}%`;

function pathFor(data: (number | null)[], x: (i: number) => number, y: (v: number) => number) {
  let d = "";
  let pen = false;
  data.forEach((v, i) => {
    if (v === null) {
      pen = false;
      return;
    }
    d += `${pen ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
    pen = true;
  });
  return d;
}

/** Approval over time: one line per series, a crosshair and tooltip on hover. */
export function TrendChart({
  series,
  labels,
  height = 200,
}: {
  series: Series[];
  /** One label per point (dates). */
  labels: string[];
  height?: number;
}) {
  const w = 600;
  const h = height;
  const pad = { l: 36, r: 14, t: 10, b: 24 };
  const iw = w - pad.l - pad.r;
  const ih = h - pad.t - pad.b;
  const n = labels.length;
  const values = series.flatMap((s) => s.data.filter((v): v is number => v !== null));
  // Floor the axis at the nearest 10% below the lowest value so changes are visible.
  const min = values.length ? Math.max(0, Math.floor(Math.min(...values) * 10) / 10 - 0.1) : 0;
  const max = 1;
  const x = (i: number) => pad.l + (n > 1 ? (iw * i) / (n - 1) : iw / 2);
  const y = (v: number) => pad.t + ih * (1 - (v - min) / (max - min));
  const ticks: number[] = [];
  for (let t = Math.ceil(min * 10) / 10; t <= max + 1e-9; t += min < 0.5 ? 0.2 : 0.1) ticks.push(t);
  const dateTicks =
    n > 1 ? [0, Math.round((n - 1) / 3), Math.round((2 * (n - 1)) / 3), n - 1] : [0];

  const [hover, setHover] = React.useState<{ i: number; left: number; top: number } | null>(null);
  const svgRef = React.useRef<SVGSVGElement>(null);
  const onMove = (e: React.MouseEvent) => {
    const b = svgRef.current!.getBoundingClientRect();
    const px = ((e.clientX - b.left) * w) / b.width;
    const i = Math.max(0, Math.min(n - 1, Math.round(((px - pad.l) / iw) * (n - 1))));
    setHover({ i, left: e.clientX - b.left, top: e.clientY - b.top });
  };

  if (!values.length) {
    return (
      <div
        className="flex items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground"
        style={{ height }}
      >
        No ratings in this period.
      </div>
    );
  }

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${w} ${h}`}
        className="h-auto w-full overflow-visible"
        role="img"
        aria-label={series
          .map((s) => {
            const last = [...s.data].reverse().find((v) => v !== null);
            return `${s.name}: latest ${last == null ? "no data" : pct(last)}`;
          })
          .join("; ")}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={pad.l}
              x2={w - pad.r}
              y1={y(t)}
              y2={y(t)}
              className="stroke-border"
              strokeWidth={1}
            />
            <text
              x={pad.l - 6}
              y={y(t) + 3}
              textAnchor="end"
              className="fill-muted-foreground font-mono text-[10px]"
            >
              {Math.round(t * 100)}
            </text>
          </g>
        ))}
        {dateTicks.map((i) => (
          <text
            key={i}
            x={x(i)}
            y={h - 6}
            textAnchor="middle"
            className="fill-muted-foreground font-mono text-[10px]"
          >
            {labels[i]}
          </text>
        ))}
        {series.map((s) => (
          <g key={s.name}>
            <path
              d={pathFor(s.data, x, y)}
              fill="none"
              stroke={s.color}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {s.data.map((v, i) =>
              v === null ? null : (
                <circle
                  key={i}
                  cx={x(i)}
                  cy={y(v)}
                  // A day with no rated neighbours has no line through it: draw it larger.
                  r={hover?.i === i || (s.data[i - 1] == null && s.data[i + 1] == null) ? 4 : 2.5}
                  fill={s.color}
                  className="stroke-card"
                  strokeWidth={1.5}
                />
              ),
            )}
          </g>
        ))}
        {hover ? (
          <line
            x1={x(hover.i)}
            x2={x(hover.i)}
            y1={pad.t}
            y2={pad.t + ih}
            className="stroke-muted-foreground"
            strokeOpacity={0.5}
          />
        ) : null}
        <rect
          x={pad.l}
          y={pad.t}
          width={iw}
          height={ih}
          fill="transparent"
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        />
      </svg>
      {hover ? (
        <div
          className="pointer-events-none absolute z-10 rounded-md bg-foreground px-2 py-1 text-xs text-background shadow"
          style={{ left: Math.min(hover.left + 12, 9999), top: hover.top + 12 }}
        >
          <div className="font-semibold">{labels[hover.i]}</div>
          {series.map((s) => (
            <div key={s.name}>
              {s.name}: {s.data[hover.i] == null ? "no ratings" : pct(s.data[hover.i]!)}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function Legend({ series }: { series: Pick<Series, "name" | "color">[] }) {
  return (
    <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
      {series.map((s) => (
        <span key={s.name} className="inline-flex items-center gap-1.5">
          <span className="inline-block h-[3px] w-3.5 rounded" style={{ background: s.color }} />
          {s.name}
        </span>
      ))}
    </div>
  );
}

/** A small trend line for a card; gaps where a day has no ratings. */
export function Sparkline({ data, color }: { data: (number | null)[]; color: string }) {
  const w = 120;
  const h = 32;
  const vals = data.filter((v): v is number => v !== null);
  if (vals.length < 2) {
    return <div className="h-8 text-xs text-muted-foreground">Not enough ratings for a trend</div>;
  }
  const mn = Math.min(...vals) - 0.02;
  const mx = Math.max(...vals) + 0.02;
  const x = (i: number) => (w * i) / (data.length - 1);
  const y = (v: number) => h - 3 - ((h - 6) * (v - mn)) / (mx - mn || 1);
  const lastIdx = data.map((v, i) => (v === null ? -1 : i)).reduce((a, b) => Math.max(a, b), -1);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} aria-hidden="true">
      <path d={pathFor(data, x, y)} fill="none" stroke={color} strokeWidth={1.5} />
      {lastIdx >= 0 ? <circle cx={x(lastIdx)} cy={y(data[lastIdx]!)} r={2.5} fill={color} /> : null}
    </svg>
  );
}

/** Horizontal bars with the value at the end (counts or rates). */
export function HBars({
  rows,
  format = (v: number) => String(v),
  max,
}: {
  rows: { label: string; value: number | null; hint?: string }[];
  format?: (v: number) => string;
  max?: number;
}) {
  const top = max ?? Math.max(1, ...rows.map((r) => r.value ?? 0));
  return (
    <div className="grid gap-2">
      {rows.map((r) => (
        <div
          key={r.label}
          className="grid grid-cols-[120px_minmax(0,1fr)_44px] items-center gap-2 text-xs"
          title={r.hint}
        >
          <span className="truncate">{r.label}</span>
          <span className="h-2.5 overflow-hidden rounded bg-muted">
            <span
              className="block h-full rounded-r"
              style={{
                width: `${r.value == null ? 0 : (100 * r.value) / top}%`,
                background: "var(--series-1)",
              }}
            />
          </span>
          <span className="text-right tabular-nums text-muted-foreground">
            {r.value == null ? "—" : format(r.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

export type EvalStatus = "healthy" | "watch" | "attention" | "no_data";

const STATUS: Record<EvalStatus, { label: string; cls: string; mark: string }> = {
  healthy: {
    label: "Healthy",
    cls: "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]",
    mark: "●",
  },
  watch: {
    label: "Watch",
    cls: "bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))]",
    mark: "▲",
  },
  // Not `text-destructive`: its dark-theme step is too dark to read as text.
  attention: { label: "Needs attention", cls: "bg-red-500/15 text-red-500", mark: "■" },
  no_data: { label: "No ratings yet", cls: "bg-muted text-muted-foreground", mark: "○" },
};

/** Health from approval: colour, a shape and a word, never colour alone. */
export function StatusChip({ status, className }: { status: EvalStatus; className?: string }) {
  const s = STATUS[status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium",
        s.cls,
        className,
      )}
    >
      <span aria-hidden="true" className="text-[10px]">
        {s.mark}
      </span>
      {s.label}
    </span>
  );
}

export const asPct = (v: number | null | undefined) => (v == null ? "—" : pct(v));
